import { isFunction } from 'lodash';
import { getConsentPopupBlockedPromise, getConsentSettledPromise } from 'modules/consent/consent';
import { createControlledPromise } from 'utils/createControlledPromise';
import { wait } from 'utils/wait';
import { CLEANUP_EVENT, GTM_READY_TIMEOUT_MS } from './constants';
import {
  GroupEventName,
  GroupName,
  GroupTrackingEvent,
  NormalizedEvent,
  Session,
  SessionEventName,
  SessionTrackingEvent,
  StandaloneEventName,
  StandaloneTrackingEvent,
  TrackingEvent,
} from './events';
import { EventCallbackResult, EventMetadata, GtmId, Snapshot, TrackConfig } from './types';
import { getEventProcessingStatus } from './utils/getEventProcessingStatus';
import { getGtmReadyPromise } from './utils/getGtmReadyPromise';
import { normalizeEvent } from './utils/normalizeEvent';
import { sortEvents } from './utils/sortEvents';

const CHANGE_EVENT = new Event('change');

export class GtmTracking extends EventTarget {
  private isGtmInitialized = false;
  private isSessionInitialized = false;
  private isSessionEventAvailablePromise = createControlledPromise();
  private events: NormalizedEvent[] = [];
  private eventsMetadata: Map<NormalizedEvent, EventMetadata> = new Map();

  constructor() {
    super();

    if (typeof window === 'undefined') {
      return;
    }

    const isGtmReadyPromise = Promise.race([getGtmReadyPromise(), wait(GTM_READY_TIMEOUT_MS)]);
    const isConsentSettledPromise = Promise.any([
      getConsentSettledPromise(),
      getConsentPopupBlockedPromise(),
    ]);

    Promise.all([isGtmReadyPromise, isConsentSettledPromise]).then(() => {
      this.isGtmInitialized = true;
      this.flushEvents();
    });

    Promise.all([this.isSessionEventAvailablePromise, isConsentSettledPromise]).then(() => {
      this.isSessionInitialized = true;
      this.flushEvents();
    });
  }

  private flushEvents(): void {
    const eventsToTrack = this.events.filter((event) => {
      const metadata = this.eventsMetadata.get(event)!;
      return (
        metadata.status === 'registered' && (!metadata.requiresSession || this.isSessionInitialized)
      );
    });
    const sortedEvents = sortEvents(eventsToTrack);
    sortedEvents.forEach((event) => this.processEvent(event));
  }

  private patchEventMetadata(
    event: NormalizedEvent,
    patch: Partial<EventMetadata> | ((metadata: EventMetadata) => Partial<EventMetadata>)
  ): void {
    const metadata = this.eventsMetadata.get(event)!;

    if (isFunction(patch)) {
      this.eventsMetadata.set(event, { ...metadata, ...patch(metadata) });
    } else {
      this.eventsMetadata.set(event, { ...metadata, ...patch });
    }
  }

  private processEvent(event: NormalizedEvent): void {
    const dataLayerIndex = window.dataLayer.length;
    window.dataLayer.push({
      ...event,
      eventCallback: (containerId: GtmId, { tags = [] }: EventCallbackResult = {}) => {
        this.patchEventMetadata(event, (metadata) => ({
          processingResults: {
            ...metadata.processingResults,
            [containerId]: {
              status: getEventProcessingStatus(tags),
              date: new Date(),
              tags,
            },
          },
          processedAt: new Date(),
          status: 'processed',
        }));
        this.dispatchEvent(CHANGE_EVENT);
        this.eventsMetadata.get(event)?.promise.resolve();
      },
    });
    window.dataLayer.push(CLEANUP_EVENT);

    this.patchEventMetadata(event, {
      dataLayerIndex,
      status: 'queued',
      queuedAt: new Date(),
    });
    this.dispatchEvent(CHANGE_EVENT);
  }

  public track<Name extends StandaloneEventName>(
    event: StandaloneTrackingEvent<Name>,
    config?: TrackConfig
  ): Promise<void>;
  public track<Group extends GroupName, Name extends GroupEventName<Group>>(
    event: GroupTrackingEvent<Group, Name>,
    config?: TrackConfig
  ): Promise<void>;
  public track(event: SessionTrackingEvent, config?: TrackConfig): Promise<void>;
  public track(event: TrackingEvent, config?: TrackConfig): Promise<void> {
    const { timeout, requiresSession = true } = config || {};
    const normalizedEvent = normalizeEvent(event);

    this.events.push(normalizedEvent);
    const promise = createControlledPromise();
    this.eventsMetadata.set(normalizedEvent, {
      promise,
      status: 'registered',
      registeredAt: new Date(),
      processingResults: {},
      requiresSession,
    });
    this.dispatchEvent(CHANGE_EVENT);

    if (this.isGtmInitialized && (this.isSessionInitialized || !requiresSession)) {
      this.processEvent(normalizedEvent);
    }

    return timeout ? Promise.race([promise, wait(timeout)]) : promise;
  }

  public session(session: Partial<Session>): void {
    this.track({ name: SessionEventName.SetSession, ...session });

    if (session.clientIp) {
      this.isSessionEventAvailablePromise.resolve();
    }
  }

  public getSnapshot(): Snapshot {
    return this.events.map((event) => {
      const metadata = this.eventsMetadata.get(event)!;
      const dataLayerEntry = metadata?.dataLayerIndex
        ? window.dataLayer.at(metadata.dataLayerIndex)
        : null;
      return { event, metadata, dataLayerEntry };
    });
  }
}
